// utils/combinedKeywordExtractor.js
import nlp from 'compromise';
import { extractKeywords as extractWithRake } from './rake';
import { extractKeywordsWithTextRank } from './textRank';
import { extractMainTopic } from './topicExtractor';
import levenshtein from 'fast-levenshtein'; // Fixed import syntax

/**
 * List of common stop words that should be filtered out from keywords
 */
const STOP_WORDS = new Set([
  // Articles
  'a', 'an', 'the',
  // Pronouns
  'he', 'she', 'it', 'they', 'we', 'i', 'you', 'me', 'him', 'her', 'them', 'us', 'my', 'your', 'his', 'their', 'our',
  // Prepositions
  'in', 'on', 'at', 'by', 'for', 'with', 'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'to', 'from', 'up', 'down', 'of',
  // Conjunctions
  'and', 'but', 'or', 'nor', 'so', 'yet', 'because', 'although', 'since', 'unless',
  // Common verbs
  'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might',
  // Other common words
  'this', 'that', 'these', 'those', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'any', 'both', 'each', 'few', 'more', 'most', 'some', 'such', 'no', 'not', 'only', 'own', 'same', 'too', 'very',
  // Blank space and HTML entities representing spaces
  '', ' ', '&nbsp;'
]);

/**
 * Special character regex patterns that should be excluded
 */
const FILE_PATH_REGEX = /[\/\\│├└─┘┌┐│║▌▐┤├┬┴┼]+/g;
const CODE_SYMBOL_REGEX = /[`~!@#$%^&*()<>{}[\]|\\\/]+/g;
const BULLET_POINT_REGEX = /[•◦‣⁃⋄◆◇◈⋅·⦿⦾⚫⚪♦♢★☆▪▫■□▶►]+/g;
const MARKDOWN_FORMATTING_REGEX = /[\*_~`#]+/g; // For asterisks and other markdown symbols
const EMOJI_REGEX = /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu;
const NEWLINE_REGEX = /\\n|\n|\r/g;
const CHECKLIST_REGEX = /(\*\*)?#\s*checklist.*(\*\*)?/i;
const TASK_LIST_REGEX = /(\*\*)?-\s*task\s*\d+.*(\*\*)?/i;

/**
 * Helper function to normalize names (e.g., remove punctuation, lowercasing, etc.)
 */
function normalizeEntity(entity) {
  return entity
    .toLowerCase()             // Convert to lowercase
    .replace(/[^\w\s]/g, '');   // Remove special characters (e.g., &nbsp;, ', etc.)
}

/**
 * Helper function to check for similarity between two entities
 * Returns true if entities are similar enough to be considered duplicates
 */
function areEntitiesSimilar(entity1, entity2, threshold = 3) {
    // Levenshtein distance: Smaller number indicates more similarity
    return levenshtein.get(normalizeEntity(entity1), normalizeEntity(entity2)) <= threshold;
}

/**
 * Clean text by removing HTML entities and normalizing spaces
 * @param {string} text - The input text
 * @returns {string} - Cleaned text
 */
function cleanText(text) {
  if (!text) return '';
  
  return text
    .replace(/&nbsp;/g, ' ')              // Replace HTML non-breaking spaces
    .replace(/&[a-z]+;/g, ' ')            // Replace other HTML entities
    .replace(FILE_PATH_REGEX, ' ')        // Remove file path characters
    .replace(CODE_SYMBOL_REGEX, ' ')      // Remove code symbols
    .replace(BULLET_POINT_REGEX, ' ')     // Remove bullet points and similar characters
    .replace(MARKDOWN_FORMATTING_REGEX, ' ') // Remove markdown formatting
    .replace(EMOJI_REGEX, ' ')            // Remove emojis
    .replace(NEWLINE_REGEX, ' ')          // Replace newlines with spaces
    .replace(/^the\s+/i, '')              // Remove leading "The" from phrases
    .replace(/['']s\b/g, '')              // Remove possessive 's
    .replace(/s['']\b/g, 's')             // Normalize plural possessives
    .replace(/[,;:.!?()[\]{}'"]+/g, ' ')  // Remove punctuation
    .replace(/\s+/g, ' ')                 // Normalize spaces again after replacements
    .trim();                               // Remove leading/trailing spaces
}

/**
 * Combine and deduplicate named entities using normalization and similarity check
 */
function deduplicateEntities(entities) {
    const uniqueEntities = [];
    const seenEntities = new Set();  // Change from an array to a Set for better performance
  
    entities.forEach((entity) => {
      let isDuplicate = false;
  
      // Check against already seen entities for similarity
      for (const existingEntity of seenEntities) {
        if (areEntitiesSimilar(entity, existingEntity)) {
          isDuplicate = true;
          break;
        }
      }
  
      // If not a duplicate, add to the list and mark as seen
      if (!isDuplicate) {
        uniqueEntities.push(entity);
        seenEntities.add(entity); // Add entity to Set for further comparison
      }
    });
  
    return uniqueEntities;
}

/**
 * Parse entity string to clean and normalize it
 * @param {string} entity - The entity string
 * @returns {string} - Cleaned entity string
 */
function parseEntityString(entity) {
  if (!entity || typeof entity !== 'string') return '';
  
  // Special handling for entities with commas that look like lists
  if (entity.includes(',')) {
    // Extract the first part before the comma
    return entity.split(',')[0].trim();
  }
  
  // Special handling for entities with "and" that look like combinations
  if (entity.includes(' and ')) {
    // Extract the first part before "and"
    return entity.split(' and ')[0].trim();
  }
  
  // Clean the entity but preserve capitalization and some punctuation for proper nouns
  return entity
    .replace(/&nbsp;/g, ' ')    // Replace HTML non-breaking spaces
    .replace(/&[a-z]+;/g, ' ')  // Replace other HTML entities
    .replace(/\s+/g, ' ')       // Normalize multiple spaces to single space
    .trim();                    // Remove leading/trailing spaces
}


/**
 * Extract named entities using Compromise
 */
function extractEntities(text) {
    const doc = nlp(text);
    
    // Get people, places, organizations - use only the methods that your version supports
    const people = doc.people ? doc.people().out('array') : [];
    const places = doc.places ? doc.places().out('array') : [];
    const organizations = doc.organizations ? doc.organizations().out('array') : [];
    
    // Format entities as objects with text and type properties
    // Filter out items that don't look like proper entities
    const formattedEntities = [
      ...people.filter(isLikelyNamedEntity).map(person => ({ 
        text: parseEntityString(person), 
        type: 'person' 
      })),
      ...places.filter(isLikelyNamedEntity).map(place => ({ 
        text: parseEntityString(place), 
        type: 'place' 
      })),
      ...organizations.filter(isLikelyNamedEntity).map(org => ({ 
        text: parseEntityString(org), 
        type: 'organization' 
      }))
    ];
    
    // Filter out specific problematic entities
    const blacklist = ['Embrace energy', 'Wall Street Journal', 'The Wall Street Journal', 'Energy', 'Blue America'];
    const filteredEntities = formattedEntities.filter(entity => 
      !blacklist.includes(entity.text) && 
      !blacklist.some(blocked => entity.text.includes(blocked))
    );
    
    // Deduplicate entities
    return deduplicateEntitiesWithType(filteredEntities);
}

/**
 * Deduplicate entities with type information
 */
function deduplicateEntitiesWithType(entities) {
    const uniqueEntities = [];
    const seenEntityTexts = new Set();
  
    entities.forEach(entity => {
      const normalizedText = normalizeEntity(entity.text);
      let isDuplicate = false;
  
      // Check against already seen entity texts for similarity
      for (const existingText of seenEntityTexts) {
        if (areEntitiesSimilar(normalizedText, existingText)) {
          isDuplicate = true;
          break;
        }
      }
  
      // If not a duplicate, add to the list and mark as seen
      if (!isDuplicate) {
        uniqueEntities.push(entity);
        seenEntityTexts.add(normalizedText);
      }
    });
  
    return uniqueEntities;
}

/**
 * Check if text appears to be a file path or directory structure
 * @param {string} text - The text to check
 * @returns {boolean} - True if it looks like a file path
 */
function isLikelyFilePath(text) {
  if (!text) return false;
  
  // Check for common file path patterns
  return (
    text.includes('/') || 
    text.includes('\\') || 
    text.includes('.js') || 
    text.includes('.css') || 
    text.includes('.html') ||
    text.includes('.ts') ||
    text.includes('.jsx') ||
    text.includes('.tsx') ||
    /^\w+\/\w+/.test(text) || // pattern like "folder/file"
    /components\//.test(text) ||
    /src\//.test(text) ||
    /api\//.test(text)
  );
}

/**
 * Check if text is mostly special characters or formatting symbols
 * @param {string} text - The text to check  
 * @returns {boolean} - True if it contains mostly special characters
 */
function isMostlySpecialChars(text) {
  if (!text) return false;
  
  // Check if the text contains the specific pattern "│ ├──" or similar
  if (/[│├┴┬─]+/.test(text)) {
    return true;
  }
  
  // Check for markdown, emoji, and checklist patterns
  if (
    text.includes('**') || 
    text.includes('\n') || 
    text.includes('\\n') || 
    EMOJI_REGEX.test(text) ||
    CHECKLIST_REGEX.test(text) ||
    TASK_LIST_REGEX.test(text)
  ) {
    return true;
  }
  
  // Count special characters
  const specialChars = text.match(/[^a-zA-Z0-9\s]/g) || [];
  const alphaNumChars = text.match(/[a-zA-Z0-9]/g) || [];
  
  // If more than 30% are special characters, reject it
  return specialChars.length > (alphaNumChars.length * 0.3);
}

/**
 * Check if keyword candidate is worth keeping
 * @param {string} text - The keyword text
 * @param {number} minLength - Minimum length to consider
 * @param {number} maxLength - Maximum length to consider
 * @returns {boolean} - True if the keyword is valid
 */
function isValidKeyword(text, minLength = 3, maxLength = 30) {
  if (!text || typeof text !== 'string') return false;
  
  // Check for problematic patterns
  if (
    text.includes('**│ ├──**') || 
    text.includes('│ ├──') ||
    text.includes('\\n') ||
    text.includes('\n') ||
    text.includes('**#') ||
    text.includes('checklist') ||
    /task\s+\d+/.test(text) ||
    EMOJI_REGEX.test(text)
  ) {
    return false;
  }
  
  const cleaned = cleanText(text);
  
  // Check length constraints
  if (cleaned.length < minLength || cleaned.length > maxLength) return false;
  
  // Count words
  const wordCount = cleaned.split(/\s+/).length;
  
  // Limit to 4 words maximum for multi-word phrases
  if (wordCount > 4) return false;
  
  // Check if it's a stop word
  if (STOP_WORDS.has(cleaned.toLowerCase())) return false;
  
  // Must contain at least one alphabetic character
  if (!/[a-zA-Z]/.test(cleaned)) return false;
  
  // Filter out file paths and directory structures
  if (isLikelyFilePath(cleaned)) return false;
  
  // Filter out strings with too many special characters
  if (isMostlySpecialChars(cleaned)) return false;
  
  // For single words, they should have some substance
  if (wordCount === 1 && cleaned.length < 4) return false;
  
  // Check if it starts with grammatical words that suggest incomplete phrases
  const firstWord = cleaned.split(/\s+/)[0].toLowerCase();
  const invalidStarters = ['says', 'said', 'will', 'would', 'could', 'should', 'has', 'have', 
    'had', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'do', 'does', 'did', 'can', 
    'since', 'like', 'asked', 'when', 'while', 'then', 'than'];
  
  if (invalidStarters.includes(firstWord)) return false;
  
  // Check for possessive forms and normalize
  if (cleaned.endsWith("'s") || cleaned.endsWith("s'")) {
    return false; // We'll handle these in the normalization function
  }
  
  return true;
}

/**
 * Extract keywords based on structural position in the document
 */
function extractStructuralKeywords(text) {
  const lines = text.split('\n');
  const paragraphs = text.split(/\n\s*\n/);
  const structuralKeywords = [];
  
  // Extract from title (first line if it looks like a title)
  if (lines.length > 0) {
    const possibleTitle = lines[0].trim();
    if (possibleTitle.length < 100 && !possibleTitle.endsWith('.')) {
      const titleDoc = nlp(possibleTitle);
      const titleNouns = titleDoc.match('#Noun+').out('array');
      titleNouns.forEach(noun => {
        if (noun.length > 2) {
          structuralKeywords.push({ text: noun, weight: 3, source: 'title' });
        }
      });
    }
  }
  
  // Extract from first paragraph (often contains key information)
  if (paragraphs.length > 0) {
    const firstPara = paragraphs[0];
    const sentences = firstPara.split(/[.!?]+/).filter(s => s.trim().length > 0);
    
    if (sentences.length > 0) {
      const firstSentence = sentences[0].trim();
      const firstSentenceDoc = nlp(firstSentence);
      const firstSentenceNouns = firstSentenceDoc.match('#Noun+').out('array');
      
      firstSentenceNouns.forEach(noun => {
        if (noun.length > 2) {
          structuralKeywords.push({ text: noun, weight: 2, source: 'first_sentence' });
        }
      });
    }
  }
  
  return structuralKeywords;
}

/**
 * Extract multi-word expressions (n-grams)
 */
function extractNgrams(text) {
  const doc = nlp(text);
  const ngrams = [];
  
  // Extract noun phrases (often valuable multi-word concepts)
  const nounPhrases = doc.match('#Adjective? #Noun+').out('frequency');
  
  nounPhrases.forEach(phrase => {
    // Only include multi-word phrases
    if (phrase.normal.includes(' ') && phrase.count > 1) {
      ngrams.push({ 
        text: phrase.normal, 
        weight: phrase.count * 1.5,  // Boost multi-word phrases
        source: 'noun_phrase' 
      });
    }
  });
  
  return ngrams;
}

/**
 * Normalize keyword formats to ensure consistency
 * @param {Array} keywords - Array of keyword objects
 * @returns {Array} - Normalized keyword objects
 */
function normalizeKeywords(keywords) {
  // Map to track normalized keywords
  const normalizedMap = new Map();
  
  keywords.forEach(keyword => {
    // Skip invalid keywords
    if (!keyword || !keyword.text) return;
    
    // Get base form by removing possessives, etc.
    let normalizedText = cleanText(keyword.text);
    
    // Convert to lowercase for map key but keep original case for display
    const lowerKey = normalizedText.toLowerCase();
    
    if (normalizedMap.has(lowerKey)) {
      // Update existing entry with higher score if applicable
      const existing = normalizedMap.get(lowerKey);
      if (keyword.score > existing.score) {
        existing.score = keyword.score;
        
        // Merge sources
        keyword.sources.forEach(source => {
          if (!existing.sources.includes(source)) {
            existing.sources.push(source);
          }
        });
        
        // Update type if the new one is more specific
        if (keyword.type && keyword.type !== 'keyword') {
          existing.type = keyword.type;
        }
      }
    } else {
      // Add new entry to map
      normalizedMap.set(lowerKey, {
        text: normalizedText,
        score: keyword.score,
        type: keyword.type,
        sources: keyword.sources
      });
    }
  });
  
  // Convert map back to array and sort by score
  return Array.from(normalizedMap.values())
    .sort((a, b) => b.score - a.score);
}

/**
 * Filters out stop words and short words from a list of keywords
 * @param {Array} keywords - Array of keywords
 * @param {number} minLength - Minimum length for a keyword to be considered
 * @param {number} maxLength - Maximum length for a phrase to be considered a keyword
 * @returns {Array} - Filtered keywords
 */
function filterKeywords(keywords, minLength = 3, maxLength = 30) {
  return keywords.filter(keyword => {
    // If it's a string, check directly
    if (typeof keyword === 'string') {
      const cleaned = cleanText(keyword);
      return isValidKeyword(cleaned, minLength, maxLength);
    }
    
    // If it's an object with a text property, check that
    if (keyword && typeof keyword.text === 'string') {
      const cleaned = cleanText(keyword.text);
      return isValidKeyword(cleaned, minLength, maxLength);
    }
    
    return false;
  });
}

/**
 * Check if a string might be a proper named entity or just a common word/phrase
 * @param {string} entity - The string to check
 * @returns {boolean} - True if it's likely a proper named entity
 */
function isLikelyNamedEntity(entity) {
  if (!entity || typeof entity !== 'string') return false;
  
  // Parse and clean the entity
  const clean = parseEntityString(entity);
  if (clean.length < 2) return false;
  
  // Check for problematic patterns
  if (
    clean.includes('**') || 
    clean.includes('\n') || 
    clean.includes('\\n') || 
    EMOJI_REGEX.test(clean) ||
    CHECKLIST_REGEX.test(clean) ||
    TASK_LIST_REGEX.test(clean)
  ) {
    return false;
  }
  
  // A proper name usually starts with a capital letter
  // Check if the first character is uppercase and it's not ALL CAPS (which might be an acronym)
  const firstChar = clean.charAt(0);
  const isProperCase = 
    firstChar === firstChar.toUpperCase() && 
    clean !== clean.toUpperCase();
  
  // Exclude entities that start with "The" (might be publications, etc.)
  if (clean.startsWith('The ') || clean.startsWith('THE ')) {
    const withoutThe = clean.slice(4);
    // Only keep if the rest is still properly capitalized
    if (withoutThe.charAt(0) !== withoutThe.charAt(0).toUpperCase()) {
      return false;
    }
  }
  
  // Check if it's not just a common stop word that happens to be capitalized
  const isNotStopWord = !STOP_WORDS.has(clean.toLowerCase());
  
  // Check if it's not just a single common word (proper names are often multi-word)
  const isComplexOrUncommon = clean.includes(' ') || clean.length > 5;
  
  // Check that it's not just a capitalized common noun or verb
  const commonWords = ['Energy', 'Embrace', 'Security', 'Action', 'Rating', 'First', 'Asked', 'Says'];
  if (!clean.includes(' ') && commonWords.includes(clean)) {
    return false;
  }
  
  // Check word count - named entities shouldn't be too long in most cases
  const wordCount = clean.split(/\s+/).length;
  if (wordCount > 5) return false;
  
  return isProperCase && isNotStopWord && isComplexOrUncommon;
}

/**
 * Combine, deduplicate and rank keywords from all sources
 */
function combineAndRankKeywords(textRankKeywords, rakeKeywords, structuralKeywords, ngramKeywords, entities, mainTopic, options = {}) {
  const opts = {
    minKeywordLength: 3,
    maxKeywordLength: 30,
    ...options
  };
  
  // Create a map to combine and deduplicate keywords
  const keywordMap = new Map();
  
  // Process TextRank keywords (our primary source)
  textRankKeywords.forEach((keyword, index) => {
    const cleanKeyword = cleanText(keyword);
    if (!isValidKeyword(cleanKeyword, opts.minKeywordLength, opts.maxKeywordLength)) return;
    
    keywordMap.set(cleanKeyword.toLowerCase(), {
      text: cleanKeyword,
      score: 10 - (index * 0.5),  // Decrease score by position
      sources: ['textrank']
    });
  });
  
  // Add RAKE keywords
  rakeKeywords.forEach((keyword, index) => {
    const cleanKeyword = cleanText(keyword);
    if (!isValidKeyword(cleanKeyword, opts.minKeywordLength, opts.maxKeywordLength)) return;
    
    const lowerKeyword = cleanKeyword.toLowerCase();
    if (keywordMap.has(lowerKeyword)) {
      // Update existing entry
      const existingKeyword = keywordMap.get(lowerKeyword);
      existingKeyword.score += 5 - (index * 0.3);
      existingKeyword.sources.push('rake');
    } else {
      // Add new entry
      keywordMap.set(lowerKeyword, {
        text: cleanKeyword,
        score: 5 - (index * 0.3),
        sources: ['rake']
      });
    }
  });
  
  // Add structural keywords
  structuralKeywords.forEach(keyword => {
    const cleanKeyword = cleanText(keyword.text);
    if (!isValidKeyword(cleanKeyword, opts.minKeywordLength, opts.maxKeywordLength)) return;
    
    const lowerKeyword = cleanKeyword.toLowerCase();
    if (keywordMap.has(lowerKeyword)) {
      // Update existing entry
      const existingKeyword = keywordMap.get(lowerKeyword);
      existingKeyword.score += keyword.weight;
      existingKeyword.sources.push(keyword.source);
    } else {
      // Add new entry
      keywordMap.set(lowerKeyword, {
        text: cleanKeyword,
        score: keyword.weight,
        sources: [keyword.source]
      });
    }
  });
  
  // Add n-gram keywords
  ngramKeywords.forEach(keyword => {
    const cleanKeyword = cleanText(keyword.text);
    if (!isValidKeyword(cleanKeyword, opts.minKeywordLength, opts.maxKeywordLength)) return;
    
    const lowerKeyword = cleanKeyword.toLowerCase();
    if (keywordMap.has(lowerKeyword)) {
      // Update existing entry
      const existingKeyword = keywordMap.get(lowerKeyword);
      existingKeyword.score += keyword.weight;
      existingKeyword.sources.push(keyword.source);
    } else {
      // Add new entry
      keywordMap.set(lowerKeyword, {
        text: cleanKeyword,
        score: keyword.weight,
        sources: [keyword.source]
      });
    }
  });
  
  // Boost entities
  entities.forEach(entity => {
    const cleanEntity = cleanText(entity.text);
    if (!isValidKeyword(cleanEntity, opts.minKeywordLength, opts.maxKeywordLength)) return;
    
    const lowerEntity = cleanEntity.toLowerCase();
    if (keywordMap.has(lowerEntity)) {
      // Update existing entry
      const existingKeyword = keywordMap.get(lowerEntity);
      existingKeyword.score += 3;  // Boost for being an entity
      existingKeyword.type = entity.type;
      existingKeyword.sources.push('entity');
    } else {
      // Add new entry
      keywordMap.set(lowerEntity, {
        text: cleanEntity,
        score: 3,
        type: entity.type,
        sources: ['entity']
      });
    }
  });
  
  // Boost terms related to main topic
  if (mainTopic && mainTopic.topic) {
    const cleanMainTopic = cleanText(mainTopic.topic);
    const mainTopicWords = cleanMainTopic.toLowerCase().split(/\s+/)
      .filter(word => !STOP_WORDS.has(word) && word.length >= opts.minKeywordLength);
    
    keywordMap.forEach((keyword, key) => {
      // If keyword is part of or related to main topic, boost it
      mainTopicWords.forEach(topicWord => {
        if (key.includes(topicWord) || topicWord.includes(key)) {
          keyword.score += 2;
          if (!keyword.sources.includes('topic_related')) {
            keyword.sources.push('topic_related');
          }
        }
      });
    });
    
    // Add main topic if not already present and it's valid
    if (isValidKeyword(cleanMainTopic, opts.minKeywordLength, opts.maxKeywordLength)) {
      const lowerTopic = cleanMainTopic.toLowerCase();
      if (!keywordMap.has(lowerTopic)) {
        keywordMap.set(lowerTopic, {
          text: cleanMainTopic,
          score: 8,
          sources: ['main_topic']
        });
      }
    }
  }
  
  // Convert map to array and sort by score
  const combinedKeywords = Array.from(keywordMap.values())
    .sort((a, b) => b.score - a.score)
    .map(item => ({
      text: item.text,
      score: parseFloat(item.score.toFixed(1)), // Round score to 1 decimal place
      type: item.type || 'keyword',
      sources: item.sources
    }));
  
  return combinedKeywords;
}

/**
 * Combined keyword extraction using multiple algorithms
 * @param {string} text - The input text
 * @param {Object} options - Extraction options
 * @returns {Object} - Extracted keywords and metadata
 */
export const extractKeywordsCombined = (text, options = {}) => {
  if (!text || text.trim().length === 0) {
    return { 
      keywords: [], 
      entities: [], 
      mainTopic: null, 
      topicConfidence: 0, 
      executionTimeMs: 0,
      wordCount: 0
    };
  }
  
  // Default options
  const defaultOptions = {
    minKeywordLength: 3,
    maxKeywordLength: 30,
    maxKeywords: 20,
    maxWordCount: 4
  };
  
  const opts = { ...defaultOptions, ...options };
  
  // Calculate word count
  const wordCount = text.trim().split(/\s+/).length;
  
  const start = performance.now();
  console.log("Starting combined keyword extraction...");
  
  // Clean the input text first
  const cleanedText = cleanText(text);
  
  // 1. Extract entities using Compromise (with deduplication)
  const entities = extractEntities(cleanedText);
  
  // Filter out entities with stop words or that are too short/long
  const filteredEntities = entities.filter(entity => 
    !isStopWordOrTooShort(entity.text, opts.minKeywordLength) && 
    entity.text.length <= opts.maxKeywordLength &&
    entity.text.split(/\s+/).length <= opts.maxWordCount
  );
  
  // 2. Extract keywords using TextRank (our best performer)
  const textRankKeywords = filterKeywords(
    extractKeywordsWithTextRank(cleanedText), 
    opts.minKeywordLength, 
    opts.maxKeywordLength
  );
  
  // 3. Extract main topic for context
  const mainTopic = extractMainTopic(cleanedText);
  
  // 4. Extract additional keywords with RAKE for comparison/backup
  const rakeKeywords = filterKeywords(
    extractWithRake(cleanedText),
    opts.minKeywordLength,
    opts.maxKeywordLength
  );
  
  // 5. Apply structural analysis to identify important terms by position
  const structuralKeywords = filterKeywords(
    extractStructuralKeywords(cleanedText),
    opts.minKeywordLength,
    opts.maxKeywordLength
  );
  
  // 6. Extract n-grams (multi-word phrases)
  const ngramKeywords = filterKeywords(
    extractNgrams(cleanedText),
    opts.minKeywordLength,
    opts.maxKeywordLength
  );
  
  // 7. Combine, deduplicate, and rank all extracted keywords
  const combinedKeywords = combineAndRankKeywords(
    textRankKeywords,
    rakeKeywords,
    structuralKeywords,
    ngramKeywords,
    filteredEntities,
    mainTopic,
    { 
      minKeywordLength: opts.minKeywordLength, 
      maxKeywordLength: opts.maxKeywordLength 
    }
  );
  
  // 8. Normalize keywords to ensure consistent format
  const normalizedKeywords = normalizeKeywords(combinedKeywords);
  
  // Performance metrics
  const end = performance.now();
  console.log(`Extraction completed in ${end - start}ms`);
  
  return {
    keywords: normalizedKeywords.slice(0, opts.maxKeywords),
    entities: filteredEntities,
    mainTopic: mainTopic && mainTopic.topic ? cleanText(mainTopic.topic) : null,
    topicConfidence: mainTopic && mainTopic.confidence ? mainTopic.confidence : 0,
    executionTimeMs: end - start,
    wordCount: wordCount
  };
};

/**
 * Check if a word is a stop word or too short to be meaningful
 * @param {string} word - The word to check
 * @param {number} minLength - Minimum length for a word to be considered (default: 3)
 * @returns {boolean} - True if the word should be filtered out
 */
function isStopWordOrTooShort(word, minLength = 3) {
  const normalized = word.toLowerCase().trim();
  return normalized.length < minLength || STOP_WORDS.has(normalized);
}